import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Box,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import { Close, Mic, Clear, Fullscreen, FullscreenExit, Add, ErrorOutline, WarningAmber, InfoOutlined, TaskAlt } from '@mui/icons-material';
import SavingOverlay from '../SavingOverlay';
import TherapyGoalSummary from '../TherapyGoalSummary';
import EvolutionCatalogQuickAdd, { QuickAddKind } from '../evolution/EvolutionCatalogQuickAdd';
import { Toast } from '../Toast';
import { useToast } from '../../hooks/useToast';
import { colors, typography } from '../../theme/designSystem';
import {
  getDefaultTextFieldSx,
  getDefaultInputLabelProps,
  getMultilineTextFieldSx,
  getMultilineWithAdornmentSx,
  getMultilineInputLabelProps,
} from '../../theme/textFieldStyles';
import {
  patientEvolutionsService,
  mapPatientEvolutionError,
  PatientEvolution,
  PatientEvolutionOptions,
  PatientEvolutionPayload,
  CatalogOption,
  QuickCreatedCatalogItem,
  EVOLUTION_TEXT_MAX_LENGTH,
  EVOLUTION_GUIDANCE_MAX_LENGTH,
  EVOLUTION_OBSERVATION_MAX_LENGTH,
  EVOLUTION_JUSTIFICATION_MAX_LENGTH,
  EVOLUTION_SCALE_MIN,
  EVOLUTION_SCALE_MAX,
  EVOLUTION_KINDS,
  EvolutionKind,
} from '../../services/patientEvolutionsService';

/**
 * Modal de registro/edição de evolução do paciente, sobre a API real. O componente é
 * remontado a cada abertura (`key` no pai): estado, opções e ditado nascem limpos.
 *
 * Ditado por voz (Web Speech API): preservado da versão anterior — reconhecimento contínuo
 * em pt-BR, texto provisório exibido em tempo real, desligamento automático após 1 minuto
 * sem fala e reinício silencioso quando o navegador encerra a sessão sozinho.
 */
interface Props {
  isOpen: boolean;
  patientId: string;
  /** null = nova evolução */
  evolution: PatientEvolution | null;
  /** Nova evolução já com a sessão escolhida (atalhos de relatório); ignorado na edição */
  initialScheduleId?: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}

type VoiceField = 'therapeuticEvolution' | 'guidance' | 'observation';

interface FormState {
  /** RN-542k: evolução de sessão ou avulsa */
  kind: EvolutionKind;
  /** Evolução avulsa: data do registro (yyyy-MM-dd) */
  recordDate: string;
  scheduleId: string;
  disregardPlan: boolean;
  planId: string;
  planEvolutionScale: string;
  gasScale: string;
  patientConditionId: string;
  patientSituationId: string;
  patientConditionJustification: string;
  aproachId: string;
  therapeuticResourceId: string;
  therapeuticEvolution: string;
  guidance: string;
  observation: string;
}

const VOICE_INACTIVITY_MS = 60000;
const VOICE_MAX_LENGTH: Record<VoiceField, number> = {
  therapeuticEvolution: EVOLUTION_TEXT_MAX_LENGTH,
  guidance: EVOLUTION_GUIDANCE_MAX_LENGTH,
  observation: EVOLUTION_OBSERVATION_MAX_LENGTH,
};
const SCALE_VALUES = Array.from(
  { length: EVOLUTION_SCALE_MAX - EVOLUTION_SCALE_MIN + 1 },
  (_, i) => EVOLUTION_SCALE_MIN + i,
);

const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const timeOf = (iso: string): string => iso.slice(11, 16);

/** Hoje no relógio do navegador, yyyy-MM-dd (o servidor confere no relógio da entidade) */
const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = (): FormState => ({
  kind: 'session',
  recordDate: todayIso(),
  scheduleId: '',
  disregardPlan: false,
  planId: '',
  planEvolutionScale: '',
  gasScale: '',
  patientConditionId: '',
  patientSituationId: '',
  patientConditionJustification: '',
  aproachId: '',
  therapeuticResourceId: '',
  therapeuticEvolution: '',
  guidance: '',
  observation: '',
});

const formFromEvolution = (e: PatientEvolution): FormState => ({
  kind: e.kind ?? 'session',
  recordDate: e.sessionDate.slice(0, 10),
  // Evoluções herdadas podem não ter agendamento (30 na base): o select nasce vazio
  scheduleId: e.scheduleId ?? '',
  disregardPlan: e.disregardPlan,
  planId: e.planId ?? '',
  planEvolutionScale: e.planEvolutionScale === null ? '' : String(e.planEvolutionScale),
  gasScale: e.gasScale ?? '',
  patientConditionId: e.patientConditionId ?? '',
  patientSituationId: e.patientSituationId ?? '',
  patientConditionJustification: e.patientConditionJustification ?? '',
  aproachId: e.aproachId ?? '',
  therapeuticResourceId: e.therapeuticResourceId ?? '',
  therapeuticEvolution: e.therapeuticEvolution ?? '',
  guidance: e.guidance ?? '',
  observation: e.observation ?? '',
});

const toPayload = (f: FormState): PatientEvolutionPayload => (f.kind === 'standalone'
  // Avulsa: só o texto da evolução e a data do registro; o resto não existe neste tipo
  ? {
    kind: 'standalone',
    recordDate: f.recordDate,
    scheduleId: null,
    disregardPlan: true,
    planId: null,
    planEvolutionScale: null,
    gasScale: null,
    patientConditionId: null,
    patientSituationId: null,
    patientConditionJustification: null,
    aproachId: null,
    therapeuticResourceId: null,
    therapeuticEvolution: f.therapeuticEvolution.trim(),
    guidance: null,
    observation: null,
  }
  : {
  kind: 'session',
  scheduleId: f.scheduleId,
  disregardPlan: f.disregardPlan,
  planId: f.disregardPlan ? null : f.planId || null,
  planEvolutionScale: f.disregardPlan || f.planEvolutionScale === '' ? null : Number(f.planEvolutionScale),
  gasScale: f.gasScale || null,
  patientConditionId: f.patientConditionId || null,
  patientSituationId: f.patientSituationId || null,
  patientConditionJustification: f.patientConditionJustification.trim() || null,
  aproachId: f.aproachId || null,
  therapeuticResourceId: f.therapeuticResourceId || null,
  therapeuticEvolution: f.therapeuticEvolution.trim(),
  guidance: f.guidance.trim() || null,
  observation: f.observation.trim() || null,
});

const isStandaloneForm = (f: FormState): boolean => f.kind === 'standalone';

/**
 * Ditado por voz sobre a Web Speech API. `append` recebe o texto final reconhecido e o
 * campo de destino; o texto provisório fica em `interimText` para exibição em tempo real.
 * As refs evitam recriar a instância a cada mudança de estado (o reconhecimento é único).
 */
const useVoiceDictation = (append: (field: VoiceField, text: string) => void) => {
  const [isRecording, setIsRecording] = useState(false);
  const [activeField, setActiveField] = useState<VoiceField | null>(null);
  const [interimText, setInterimText] = useState('');
  const [notice, setNotice] = useState('');
  const [supported, setSupported] = useState(true);

  const recognitionRef = useRef<any>(null);
  const activeFieldRef = useRef<VoiceField | null>(null);
  const isRecordingRef = useRef(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appendRef = useRef(append);
  appendRef.current = append;

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const resetState = useCallback(() => {
    isRecordingRef.current = false;
    activeFieldRef.current = null;
    setIsRecording(false);
    setActiveField(null);
    setInterimText('');
  }, []);

  const stop = useCallback(() => {
    clearInactivityTimer();
    resetState();
    try {
      recognitionRef.current?.stop();
    } catch {
      // já parado
    }
  }, [resetState]);

  // Sem fala por 1 minuto: desliga sozinho (evita microfone aberto esquecido)
  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      stop();
      setNotice('Gravação desligada automaticamente após 1 minuto sem fala.');
    }, VOICE_INACTIVITY_MS);
  }, [stop]);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSupported(false);
      return undefined;
    }

    let instance: any;
    try {
      instance = new SpeechRecognition();
    } catch {
      setSupported(false);
      return undefined;
    }
    instance.continuous = true;
    instance.interimResults = true;
    instance.lang = 'pt-BR';

    instance.onstart = () => startInactivityTimer();

    instance.onresult = (event: any) => {
      const field = activeFieldRef.current;
      if (!field) return;
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += transcript + ' ';
        else interim += transcript;
      }
      startInactivityTimer();
      if (interim) setInterimText(interim);
      if (final) {
        appendRef.current(field, final);
        setInterimText('');
      }
    };

    instance.onerror = (event: any) => {
      clearInactivityTimer();
      const messages: Record<string, string> = {
        'not-allowed': 'Permissão de microfone negada. Permita o acesso ao microfone nas configurações do navegador.',
        'no-speech': 'Nenhuma fala detectada. Fale mais próximo do microfone.',
        network: 'Erro de rede no reconhecimento de voz. Verifique a conexão com a internet.',
      };
      setNotice(messages[event?.error] || 'Erro no reconhecimento de voz.');
      resetState();
    };

    // O navegador encerra a sessão sozinho de tempos em tempos: enquanto o usuário não
    // parou, reinicia em silêncio para o ditado continuar
    instance.onend = () => {
      if (!isRecordingRef.current) return;
      try {
        instance.start();
      } catch {
        clearInactivityTimer();
        resetState();
      }
    };

    recognitionRef.current = instance;

    return () => {
      clearInactivityTimer();
      isRecordingRef.current = false;
      try {
        instance.stop();
      } catch {
        // já parado
      }
    };
  }, [startInactivityTimer, resetState]);

  const toggle = (field: VoiceField) => {
    setNotice('');
    if (isRecording && activeField === field) {
      stop();
      return;
    }
    if (isRecording) return;
    if (!recognitionRef.current) {
      setNotice('Seu navegador não reconhece voz. Para ditar, use o Chrome ou o Edge.');
      return;
    }
    activeFieldRef.current = field;
    isRecordingRef.current = true;
    setActiveField(field);
    setIsRecording(true);
    setInterimText('');
    try {
      recognitionRef.current.start();
    } catch (error: any) {
      clearInactivityTimer();
      resetState();
      setNotice(String(error?.message || '').includes('already started')
        ? 'O reconhecimento de voz já está ativo. Aguarde um instante.'
        : 'Não foi possível iniciar a gravação. Verifique as permissões do microfone e tente novamente.');
    }
  };

  return { isRecording, activeField, interimText, notice, supported, toggle, stop };
};

const EvolutionModal: React.FC<Props> = ({ isOpen, patientId, evolution, initialScheduleId, onClose, onSaved }) => {
  const [options, setOptions] = useState<PatientEvolutionOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState<FormState>(() => (
    evolution ? formFromEvolution(evolution) : { ...emptyForm(), scheduleId: initialScheduleId ?? '' }
  ));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Realizadas = presença com significado "atendido"; Todas = o que a regra da evolução aceita
  const [scheduleFilter, setScheduleFilter] = useState<'attended' | 'all'>('attended');
  // Desmarcado por padrão: o profissional afirma o atendimento; sem isso a presença não muda
  const [markAttended, setMarkAttended] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [quickAdd, setQuickAdd] = useState<QuickAddKind | null>(null);
  const { toast, showToast, hideToast } = useToast();

  const appendVoice = useCallback((field: VoiceField, text: string) => {
    setForm(prev => ({ ...prev, [field]: (prev[field] + text).slice(0, VOICE_MAX_LENGTH[field]) }));
  }, []);
  const voice = useVoiceDictation(appendVoice);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await patientEvolutionsService.getOptions(patientId, {
          scheduleId: evolution?.scheduleId ?? initialScheduleId,
          planId: evolution?.planId,
        });
        if (cancelled) return;
        setOptions(data);
        // Sem objetivo de plano disponível, a evolução nasce sem avaliar o plano
        if (!evolution) setForm(prev => ({ ...prev, disregardPlan: data.plans.length === 0 }));
        // Sessão pré-escolhida que não é "realizada" (ex.: cancelada com evolução permitida) só
        // aparece em "Todas" — sem isso o campo abriria vazio e a escolha do relatório se perderia
        const preEscolhida = initialScheduleId ? data.schedules.find(s => s.id === initialScheduleId) : undefined;
        if (!evolution && preEscolhida && !preEscolhida.attended) setScheduleFilter('all');
      } catch {
        if (!cancelled) setLoadError('Não foi possível carregar os dados para registrar a evolução.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [patientId, evolution, initialScheduleId]);

  const initialPayload = useMemo(
    () => (evolution ? JSON.stringify(toPayload(formFromEvolution(evolution))) : ''),
    [evolution],
  );
  const dirty = !evolution || JSON.stringify(toPayload(form)) !== initialPayload;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Evolução nova sem objetivo em andamento: não há plano a avaliar, a opção fica travada
  const planLocked = !evolution && !!options && options.plans.length === 0;

  /** Item cadastrado (ou já existente) entra no combo, se ainda não estiver, e já fica selecionado. */
  const handleQuickAdded = (kind: QuickAddKind, item: QuickCreatedCatalogItem) => {
    const listKey = kind === 'approach' ? 'aproaches' : 'resources';
    const option: CatalogOption = { id: item.id, name: item.name, onlyForMe: item.onlyForMe };
    setOptions(prev => {
      if (!prev || prev[listKey].some(o => o.id === item.id)) return prev;
      const list = [...prev[listKey], option].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      return { ...prev, [listKey]: list };
    });
    set(kind === 'approach' ? 'aproachId' : 'therapeuticResourceId', item.id);
    setQuickAdd(null);
    const message = kind === 'approach'
      ? (item.existing ? 'Já existia uma abordagem com esse nome — selecionada' : 'Abordagem cadastrada')
      : (item.existing ? 'Já existia um recurso com esse nome — selecionado' : 'Recurso cadastrado');
    showToast(message, 'success');
  };

  const catalogLabel = (o: CatalogOption) => (o.onlyForMe ? `${o.name} (só para mim)` : o.name);

  const selectedSchedule = options?.schedules.find(s => s.id === form.scheduleId);
  const attendedCount = options?.schedules.filter(s => s.attended).length ?? 0;
  // Na edição, o agendamento gravado fica na lista em qualquer filtro (senão o campo esvazia)
  const inFilter = (s: { id: string; attended: boolean }, filter: 'attended' | 'all') =>
    filter === 'all' || s.attended || s.id === evolution?.scheduleId;
  const visibleSchedules = (options?.schedules ?? []).filter(s => inFilter(s, scheduleFilter));

  // Trocar o filtro limpa a escolha que não pertence a ele: "Realizadas" não pode exibir
  // selecionada uma sessão que não foi realizada
  const changeScheduleFilter = (filter: 'attended' | 'all') => {
    setScheduleFilter(filter);
    if (selectedSchedule && !inFilter(selectedSchedule, filter)) {
      set('scheduleId', '');
      setMarkAttended(false);
    }
  };
  const selectedPlan = options?.plans.find(p => p.id === form.planId);
  const scheduleAlreadyEvolved = !!selectedSchedule?.hasEvolution && selectedSchedule.id !== evolution?.scheduleId;

  const handleClose = () => {
    if (saving) return;
    voice.stop();
    onClose();
  };

  const standalone = isStandaloneForm(form);

  const validate = (): string => {
    if (standalone) {
      if (!form.recordDate) return 'Informe a data do registro.';
      if (form.recordDate > todayIso()) return 'A data do registro não pode ser no futuro.';
      if (!form.therapeuticEvolution.trim()) return 'Escreva a evolução terapêutica.';
      return '';
    }
    if (!form.scheduleId) return 'Selecione o agendamento da sessão.';
    if (!form.disregardPlan) {
      if (!form.planId) return 'Selecione o objetivo do plano terapêutico ou marque "Desconsiderar plano terapêutico".';
      if (form.planEvolutionScale === '') return 'Informe a nota da sessão para o objetivo selecionado.';
    }
    if (!form.therapeuticEvolution.trim()) return 'Escreva a evolução terapêutica.';
    return '';
  };

  const handleSave = async () => {
    const message = validate();
    if (message) {
      setSaveError(message);
      return;
    }
    voice.stop();
    setSaving(true);
    setSaveError('');
    try {
      if (evolution) {
        await patientEvolutionsService.update(evolution.id, toPayload(form));
        onSaved('Evolução atualizada.');
      } else {
        await patientEvolutionsService.create(patientId, {
          ...toPayload(form),
          markAttended: !standalone && !!selectedSchedule?.canMarkAttended && markAttended,
        });
        onSaved('Evolução registrada.');
      }
    } catch (err: any) {
      setSaving(false);
      setSaveError(mapPatientEvolutionError(err));
    }
  };

  const selectSx = getDefaultTextFieldSx();
  const selectLabelProps = getDefaultInputLabelProps();
  const fullRow = { gridColumn: '1 / -1' };
  // Linha que termina em texto explicativo ou aviso: o rótulo flutuante do campo de baixo sobe
  // ~9px para dentro do espaço da grade, então o texto precisa de folga própria para não colar nele
  const explainedRow = { ...fullRow, pb: 1.5 };
  // Menus dos selects: abrem abaixo do campo, alinhados à esquerda (padrão do sistema) e
  // NUNCA mais largos que o campo — a opção longa quebra em linhas em vez de estourar o modal
  const selectProps = {
    MenuProps: {
      variant: 'menu' as const,
      anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
      transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
      // Teto = largura útil do modal (md = 900px menos o recuo) e, no celular, a da tela
      PaperProps: {
        sx: {
          maxHeight: 320,
          maxWidth: 'min(852px, calc(100vw - 48px))',
          '& .MuiMenuItem-root': { whiteSpace: 'normal', alignItems: 'flex-start' },
        },
      },
    },
  };
  const quickAddButton = (kind: QuickAddKind, tooltip: string) => (
    <Tooltip title={tooltip} arrow>
      {/* Ação de seção interna: botão contornado do design system (ver AddButton) */}
      <IconButton
        aria-label={tooltip}
        onClick={() => setQuickAdd(kind)}
        className="c4u-btn-icon c4u-btn-icon--outline"
        sx={{ flexShrink: 0 }}
      >
        <Add />
      </IconButton>
    </Tooltip>
  );
  // Aviso em caixa: vermelho para o que muda o registro (sessão cancelada), âmbar para o que
  // grava na agenda (atendimento afirmado), cinza para informação. Texto escuro de cada tom:
  // o vermelho padrão sobre o fundo claro não passa de 4,5:1 em texto pequeno.
  const NOTICE_TONES = {
    danger: { color: '#842029', background: colors.errorSoft, Icon: ErrorOutline },
    warning: { color: colors.warningSoftText, background: colors.warningSoft, Icon: WarningAmber },
    info: { color: colors.textSecondary, background: '#f1f3f5', Icon: InfoOutlined },
  } as const;
  const notice = (tone: keyof typeof NOTICE_TONES, text: string) => {
    const t = NOTICE_TONES[tone];
    return (
      <Box
        role={tone === 'info' ? 'status' : 'alert'}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1, px: 1.5, py: 1, borderRadius: '8px', color: t.color, backgroundColor: t.background }}
      >
        <t.Icon sx={{ fontSize: 18, mt: '1px', flex: '0 0 auto' }} aria-hidden />
        <Typography component="span" sx={{ fontSize: '0.8rem', lineHeight: 1.5, fontWeight: tone === 'info' ? 400 : 600 }}>{text}</Typography>
      </Box>
    );
  };

  // Status com a cor do catálogo numa bolinha: a cor é da clínica (pode ser amarelo-claro),
  // então ela nunca vira fundo de texto — o texto fica na cor secundária, legível em qualquer caso
  const statusTag = (label: string, value: string, color?: string | null) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
      <Box
        component="span"
        aria-hidden
        sx={{ width: 8, height: 8, borderRadius: '50%', flex: '0 0 auto', backgroundColor: color || '#adb5bd', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.18)' }}
      />
      {label}: {value}
    </Box>
  );

  const optionLine = (primary: string, secondary?: React.ReactNode) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{primary}</Typography>
      {secondary && (
        <Typography component="div" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: colors.textSecondary, overflowWrap: 'anywhere' }}>{secondary}</Typography>
      )}
    </Box>
  );

  const voiceField = (
    field: VoiceField,
    label: string,
    maxLength: number,
    extra: { required?: boolean; minRows?: number; helper?: string },
  ) => {
    const recordingHere = voice.isRecording && voice.activeField === field;
    const value = recordingHere ? form[field] + voice.interimText : form[field];
    return (
      <Box sx={fullRow}>
        <TextField
          fullWidth
          multiline
          minRows={extra.minRows ?? 3}
          required={extra.required}
          label={label}
          value={value}
          onChange={(e) => set(field, e.target.value)}
          inputProps={{ maxLength }}
          InputLabelProps={getMultilineInputLabelProps()}
          InputProps={{
            endAdornment: (
              <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: '4px' }}>
                {form[field] && (
                  <IconButton
                    size="small"
                    onClick={() => set(field, '')}
                    title="Limpar campo"
                    sx={{ color: colors.textSecondary, '&:hover': { color: colors.error, backgroundColor: 'rgba(220, 53, 69, 0.1)' } }}
                  >
                    <Clear sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => voice.toggle(field)}
                  disabled={voice.isRecording && !recordingHere}
                  title={recordingHere ? 'Parar ditado' : 'Ditar por voz'}
                  sx={{
                    color: recordingHere ? colors.error : colors.primary,
                    animation: recordingHere ? 'evolutionMicPulse 1.5s infinite' : 'none',
                    '@keyframes evolutionMicPulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                      '100%': { opacity: 1 },
                    },
                    '&.Mui-disabled': { color: colors.borderSubtle },
                  }}
                >
                  <Mic sx={{ fontSize: '1.2rem' }} />
                </IconButton>
              </Box>
            ),
          }}
          helperText={`${form[field].length}/${maxLength}${extra.helper ? ` · ${extra.helper}` : ''}`}
          sx={getMultilineWithAdornmentSx()}
        />
      </Box>
    );
  };

  return (
    <>
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '12px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          position: 'relative',
        },
      }}
    >
      <SavingOverlay open={saving} message="Gravando evolução..." />
      <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="h3" sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold, margin: 0 }}>
          <Box>
            {evolution ? 'Editar evolução' : 'Nova evolução'}
            {options?.patientName && (
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 400, opacity: 0.9, mt: 0.25 }}>
                {options.patientName}
              </Typography>
            )}
          </Box>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={fullScreen ? 'Sair da tela cheia' : 'Tela cheia'}>
            <IconButton
              onClick={() => setFullScreen(v => !v)}
              sx={{ color: colors.white, padding: '0.25rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              {fullScreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleClose} sx={{ color: colors.white, padding: '0.25rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: '1.5rem !important',
          paddingTop: '1.5rem !important',
          flex: 1,
          minWidth: 0,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: colors.primary }} />
          </Box>
        ) : loadError || !options ? (
          <Typography sx={{ color: colors.error, fontSize: '0.9rem', py: 2 }}>{loadError || 'Dados indisponíveis.'}</Typography>
        ) : (
          <>
            {saveError && (
              <Box sx={{ backgroundColor: '#fde2e4', color: colors.error, borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.85rem', mb: 2 }}>
                {saveError}
              </Box>
            )}

            <Box
              sx={{
                display: 'grid',
                // minmax(0, 1fr): sem isso a coluna cresce até o texto mais longo (o agendamento escolhido
                // não quebra linha) e o modal passa da tela no celular
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                // Contadores e dicas sob os campos: respiro antes do rótulo do campo seguinte
                '& .MuiFormHelperText-root': { mb: 0.75 },
              }}
            >
              {/* RN-542k: o tipo define o formulário; na edição não muda (a de sessão tem campos que a avulsa não tem) */}
<FormControl component="fieldset" disabled={!!evolution} sx={explainedRow}>
                <FormLabel component="legend" sx={{ fontSize: '0.8rem', color: colors.textSecondary, '&.Mui-focused': { color: colors.textSecondary } }}>
                  Tipo de evolução
                </FormLabel>
                <RadioGroup
                  row
                  value={form.kind}
                  onChange={(e) => set('kind', e.target.value as EvolutionKind)}
                  sx={{ columnGap: 2 }}
                >
                  {EVOLUTION_KINDS.map(k => (
                    <FormControlLabel
                      key={k.value}
                      value={k.value}
                      control={<Radio size="small" sx={{ '&.Mui-checked': { color: colors.primary } }} />}
                      label={k.label}
                    />
                  ))}
                </RadioGroup>
                <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                  {EVOLUTION_KINDS.find(k => k.value === form.kind)?.help}
                </Typography>
              </FormControl>

              {standalone ? (
                <>
                  <TextField
                    type="date"
                    label="Data do registro"
                    value={form.recordDate}
                    onChange={(e) => set('recordDate', e.target.value)}
                    required
                    inputProps={{ max: todayIso() }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...selectSx, ...fullRow, maxWidth: { sm: 220 } }}
                  />
                  <Typography sx={{ ...explainedRow, fontSize: typography.fontSize.xs, color: voice.notice ? '#b26a00' : colors.textSecondary }}>
                    {voice.notice
                      || (voice.supported
                        ? 'Ditado por voz: clique no microfone do campo, aguarde um instante e fale. A gravação para sozinha após 1 minuto sem fala.'
                        : 'Ditado por voz indisponível neste navegador — para ditar, use o Chrome ou o Edge. Digitar funciona normalmente.')}
                  </Typography>
                  {voiceField('therapeuticEvolution', 'Evolução terapêutica', EVOLUTION_TEXT_MAX_LENGTH, { required: true, minRows: 6 })}
                </>
              ) : (
              <>
              <Box sx={explainedRow}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={scheduleFilter}
                  onChange={(_, v) => v && changeScheduleFilter(v)}
                  aria-label="Quais agendamentos listar"
                  sx={{
                    mb: 3,
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'none' }, textTransform: 'none', px: 2 },
                    '& .MuiToggleButton-root.Mui-selected': {
                      color: colors.primary,
                      backgroundColor: `${colors.primary}14`,
                      fontWeight: 600,
                    },
                  }}
                >
                  <ToggleButton value="attended">Realizadas ({attendedCount})</ToggleButton>
                  <ToggleButton value="all">Todas ({options.schedules.length})</ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  select
                  fullWidth
                  required
                  label="Agendamento"
                  value={form.scheduleId}
                  onChange={(e) => { set('scheduleId', e.target.value); setMarkAttended(false); }}
                  InputLabelProps={selectLabelProps}
                  SelectProps={{
                    ...selectProps,
                    renderValue: (v) => {
                      const s = options.schedules.find(x => x.id === v);
                      return s ? `${formatIsoDate(s.dateTime)} ${timeOf(s.dateTime)} · ${s.serviceTypeName || '—'}` : '';
                    },
                  }}
                  helperText={scheduleAlreadyEvolved
                      ? 'Este agendamento já tem evolução registrada.'
                      : scheduleFilter === 'attended'
                        ? `Seus atendimentos com presença registrada nos últimos ${options.scheduleWindowDays} dias`
                        : `Seus agendamentos dos últimos ${options.scheduleWindowDays} dias, com ou sem presença`}
                  FormHelperTextProps={scheduleAlreadyEvolved ? { sx: { color: '#b26a00', fontWeight: 600 } } : undefined}
                  sx={selectSx}
                >
                  {visibleSchedules.length === 0 && (
                    <MenuItem value="" disabled>
                      {options.schedules.length === 0
                        ? 'Nenhum agendamento seu no período'
                        : 'Nenhuma sessão com presença registrada — veja em Todas'}
                    </MenuItem>
                  )}
                  {visibleSchedules.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {optionLine(
                        // Sem o nome do profissional: a lista só traz agendamentos de quem está logado (RN-542b)
                        `${formatIsoDate(s.dateTime)} ${timeOf(s.dateTime)} · ${s.serviceTypeName || '—'}`,
                        <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}>
                          {statusTag('Confirmação', s.confirmation || 'Pendente', s.confirmationColor)}
                          {statusTag('Presença', s.presence || 'Não informado', s.presenceColor)}
                          {s.hasEvolution && (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: '1px',
                                borderRadius: '10px', fontWeight: 600, whiteSpace: 'nowrap',
                                color: colors.warningSoftText, backgroundColor: colors.warningSoft,
                              }}
                            >
                              <TaskAlt sx={{ fontSize: 13 }} aria-hidden />
                              já evoluído
                            </Box>
                          )}
                        </Box>,
                      )}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Presença do agendamento (RN-542j): a evolução só marca atendimento quando o
                    profissional afirma, e só onde a presença ainda está em branco */}
                {!evolution && selectedSchedule && (
                  selectedSchedule.canMarkAttended ? (
                    <Box sx={{ mt: 1.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={markAttended}
                            onChange={(e) => setMarkAttended(e.target.checked)}
                            sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }}
                          />
                        }
                        label="Atendimento realizado"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem', color: colors.text, fontWeight: 600 } }}
                      />
                      {markAttended
                        ? notice('warning', 'Ao salvar, a presença desta sessão será marcada como atendida na agenda. Isso conta como sessão realizada para cobrança e repasse. Marque somente se o atendimento aconteceu.')
                        : (
                          <Typography sx={{ fontSize: typography.fontSize.xs, ml: 4, lineHeight: 1.5, color: colors.textSecondary }}>
                            Marque somente se o atendimento aconteceu. Sem marcar, a evolução é registrada e a presença na agenda não muda.
                          </Typography>
                        )}
                    </Box>
                  ) : (
                    selectedSchedule.cancelled
                      ? notice('danger', 'Sessão cancelada: a evolução é registrada sem marcar presença na agenda.')
                      : notice('info', `Presença na agenda: ${selectedSchedule.presence || 'Não informado'}. A evolução não altera a presença.`)
                  )
                )}
              </Box>



              <Box sx={explainedRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.disregardPlan}
                      disabled={planLocked}
                      onChange={(e) => set('disregardPlan', e.target.checked)}
                      sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }}
                    />
                  }
                  label="Desconsiderar plano terapêutico"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem', color: colors.text } }}
                />
                {planLocked && (
                  <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, ml: 4 }}>
                    Sem plano terapêutico em andamento com você para este paciente.
                  </Typography>
                )}
              </Box>

              {/* Ordem (Hilton, 28/09/2026): plano; situação e condição; justificativa; abordagem e
                  recurso; Escala GAS por último — o que se observou, o que se fez, o resultado */}
              {!form.disregardPlan && (
                <Box sx={fullRow}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Objetivo"
                    value={form.planId}
                    onChange={(e) => set('planId', e.target.value)}
                    InputLabelProps={selectLabelProps}
                    SelectProps={{
                      ...selectProps,
                      renderValue: (v) => options.plans.find(x => x.id === v)?.title ?? '',
                    }}
                    helperText={selectedPlan
                      ? `Estado atual ${selectedPlan.currentScale ?? '—'} → meta ${selectedPlan.targetScale ?? '—'}`
                      : options.plans.length === 0 ? 'Nenhum objetivo em andamento em plano de que você participa' : 'Objetivos dos planos de que você participa'}
                    sx={selectSx}
                  >
                    {options.plans.map(p => (
                      <MenuItem key={p.id} value={p.id}>
                        {optionLine(p.title, p.periodTitle)}
                      </MenuItem>
                    ))}
                  </TextField>
                  {form.planId && (
                    <TherapyGoalSummary patientId={patientId} planId={form.planId} />
                  )}
                </Box>
              )}

              {!form.disregardPlan && (
                <TextField
                  select
                  fullWidth
                  required
                  label="Nota da sessão"
                  value={form.planEvolutionScale}
                  onChange={(e) => set('planEvolutionScale', e.target.value)}
                  InputLabelProps={selectLabelProps}
                  SelectProps={selectProps}
                  helperText={`De ${EVOLUTION_SCALE_MIN} a ${EVOLUTION_SCALE_MAX}`}
                  sx={{ ...selectSx, ...fullRow }}
                >
                  {SCALE_VALUES.map(n => (
                    <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                fullWidth
                label="Situação do paciente"
                value={form.patientSituationId}
                onChange={(e) => set('patientSituationId', e.target.value)}
                InputLabelProps={selectLabelProps}
                SelectProps={selectProps}
                sx={selectSx}
              >
                <MenuItem value="">Não informada</MenuItem>
                {options.situations.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Condição do paciente"
                value={form.patientConditionId}
                onChange={(e) => set('patientConditionId', e.target.value)}
                InputLabelProps={selectLabelProps}
                SelectProps={selectProps}
                sx={selectSx}
              >
                <MenuItem value="">Não informada</MenuItem>
                {options.conditions.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>

              <Box sx={fullRow}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Justificativa da condição"
                  value={form.patientConditionJustification}
                  onChange={(e) => set('patientConditionJustification', e.target.value)}
                  inputProps={{ maxLength: EVOLUTION_JUSTIFICATION_MAX_LENGTH }}
                  helperText={`${form.patientConditionJustification.length}/${EVOLUTION_JUSTIFICATION_MAX_LENGTH}`}
                  InputLabelProps={getMultilineInputLabelProps()}
                  sx={getMultilineTextFieldSx()}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <TextField
                  select
                  fullWidth
                  label="Abordagem utilizada"
                  value={form.aproachId}
                  onChange={(e) => set('aproachId', e.target.value)}
                  InputLabelProps={selectLabelProps}
                  SelectProps={selectProps}
                  sx={{ ...selectSx, minWidth: 0 }}
                >
                  <MenuItem value="">Não informada</MenuItem>
                  {options.aproaches.map(a => (
                    <MenuItem key={a.id} value={a.id}>{catalogLabel(a)}</MenuItem>
                  ))}
                </TextField>
                {quickAddButton('approach', 'Cadastrar abordagem')}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <TextField
                  select
                  fullWidth
                  label="Recurso utilizado"
                  value={form.therapeuticResourceId}
                  onChange={(e) => set('therapeuticResourceId', e.target.value)}
                  InputLabelProps={selectLabelProps}
                  SelectProps={selectProps}
                  sx={{ ...selectSx, minWidth: 0 }}
                >
                  <MenuItem value="">Não informado</MenuItem>
                  {options.resources.map(r => (
                    <MenuItem key={r.id} value={r.id}>{catalogLabel(r)}</MenuItem>
                  ))}
                </TextField>
                {quickAddButton('resource', 'Cadastrar recurso')}
              </Box>

              <TextField
                select
                fullWidth
                label="Escala GAS"
                value={form.gasScale}
                onChange={(e) => set('gasScale', e.target.value)}
                InputLabelProps={selectLabelProps}
                SelectProps={selectProps}
                sx={{ ...selectSx, ...fullRow }}
              >
                <MenuItem value="">Não informada</MenuItem>
                {options.gasScales.map(g => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </TextField>

              {/* O aviso do navegador aparece SEM precisar tentar gravar: descobrir que nao da
                  para ditar depois de falar meio minuto e o pior jeito de descobrir. */}
              <Typography sx={{ ...explainedRow, fontSize: typography.fontSize.xs, color: voice.notice ? '#b26a00' : colors.textSecondary }}>
                {voice.notice
                  || (voice.supported
                    ? 'Ditado por voz: clique no microfone do campo, aguarde um instante e fale. A gravação para sozinha após 1 minuto sem fala.'
                    : 'Ditado por voz indisponível neste navegador — para ditar, use o Chrome ou o Edge. Digitar funciona normalmente.')}
              </Typography>

              {voiceField('therapeuticEvolution', 'Evolução terapêutica', EVOLUTION_TEXT_MAX_LENGTH, { required: true, minRows: 5 })}
              {voiceField('guidance', 'Orientação de conduta', EVOLUTION_GUIDANCE_MAX_LENGTH, {})}
              {voiceField('observation', 'Observações', EVOLUTION_OBSERVATION_MAX_LENGTH, { helper: 'Uso interno: não aparece na impressão' })}
              </>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: colors.textSecondary, textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !!loadError || !dirty}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}
        >
          Salvar
        </Button>
      </DialogActions>
      {quickAdd && (
        <EvolutionCatalogQuickAdd
          key={quickAdd}
          kind={quickAdd}
          open
          resourceTypes={options?.resourceTypes ?? []}
          onClose={() => setQuickAdd(null)}
          onSaved={(item) => handleQuickAdded(quickAdd, item)}
        />
      )}
    </Dialog>
    <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </>
  );
};

export default EvolutionModal;
